// frontend/src/pages/storeActivity/AccountDetails.jsx
//
// The record-level layer, collapsed by default: the store's settings and
// tokens, its billing state with the full transition trail and the FX
// behind the revenue figure, its people, and its command-palette searches.
// Kept whole for the reader who needs a raw value — and out of the way of
// everyone else.
//
// Values render as the model holds them (`product_type`, `imperial`,
// `read_products`): the raw string is what matches the model, the logs and
// the API, and prose in its place would have to be mapped back before it
// could be used for anything.

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ICON, GearSix, MagnifyingGlass, Receipt, UserCircle } from '../../icons';
import DisclosureCaret from '../../components/common/DisclosureCaret';
import { useFormatters } from '../../hooks/useFormatters';
import {
  reasonLabel, stateLabel, stateMeaning, stateSinceLabel, transitionLabel, triggerLabel,
} from './billingStateRow';
import { Def, DefGroup, Panel } from './primitives';

export default function AccountDetails({ profile, billing, people, searches, searchesTruncated }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  return (
    <section className="sa-details">
      <button type="button" className="sa-details-toggle" onClick={() => setOpen((v) => !v)} aria-expanded={open}>
        <DisclosureCaret open={open} size={ICON.lg} />
        <span className="sa-details-text">
          <span className="sa-details-title">{t('storeActivity.details.title')}</span>
          <span className="sa-details-subtitle">{t('storeActivity.details.subtitle')}</span>
        </span>
      </button>
      {open && (
        <div className="sa-grid">
          <ProfilePanel profile={profile} />
          <BillingPanel billing={billing} />
          <PeoplePanel people={people} />
          <SearchesPanel searches={searches} truncated={searchesTruncated} />
        </div>
      )}
    </section>
  );
}

function ProfilePanel({ profile }) {
  const { t } = useTranslation();
  const { formatDateTime } = useFormatters();
  const when = (value) => (value ? formatDateTime(value) : null);

  return (
    <Panel
      className="sa-panel--wide"
      icon={<GearSix size={ICON.md} />}
      title={t('storeActivity.sections.profile')}
      subtitle={t('storeActivity.sections.profileSubtitle')}
    >
      <DefGroup title={t('storeActivity.profile.groups.shop')}>
        <Def label={t('storeActivity.profile.storeType')} value={profile.store_type} />
        <Def label={t('storeActivity.profile.currency')} value={profile.currency} />
        {/* The offset, not just the zone name: it is what says whether it is
            the middle of the night for the merchant you are about to call,
            and it is read off the shop's clock now — so it is right on both
            sides of a daylight-saving change. */}
        <Def
          label={t('storeActivity.profile.timezone')}
          value={`${profile.timezone} · UTC${profile.utc_offset}`}
        />
        <Def label={t('storeActivity.profile.shopLanguages')} value={profile.shop_locales.join(' · ')} />
        <Def label={t('storeActivity.profile.appLanguage')} value={profile.language} />
        <Def label={t('storeActivity.profile.region')} value={profile.region_code} />
        <Def
          label={t('storeActivity.profile.units')}
          value={`${profile.unit_system} · ${profile.weight_unit} · ${profile.dimension_unit}`}
        />
        <Def label={t('storeActivity.profile.shopGid')} value={profile.shop_gid} />
      </DefGroup>

      <DefGroup title={t('storeActivity.profile.groups.setup')}>
        <Def label={t('storeActivity.profile.categorySource')} value={profile.category_source} />
        <Def label={t('storeActivity.profile.supplierSource')} value={profile.supplier_source} />
        <Def
          label={t('storeActivity.profile.planningHorizon')}
          value={t('storeActivity.profile.days', { count: profile.planning_horizon_days })}
        />
        <Def
          label={t('storeActivity.profile.catalogScope')}
          value={t(profile.catalog_scoped
            ? 'storeActivity.profile.catalogScoped'
            : 'storeActivity.profile.catalogWhole')}
        />
      </DefGroup>

      <DefGroup title={t('storeActivity.profile.groups.connection')}>
        <Def
          label={t('storeActivity.profile.tokenKind')}
          value={profile.token_kind ? t(`storeActivity.profile.tokenKinds.${profile.token_kind}`) : null}
        />
        <Def
          label={t('storeActivity.profile.accessTokenExpires')}
          value={when(profile.access_token_expires_at)}
        />
        <Def
          label={t('storeActivity.profile.refreshTokenExpires')}
          value={when(profile.refresh_token_expires_at)}
        />
        <Def
          label={t('storeActivity.profile.lastAuthError')}
          value={when(profile.last_auth_error_at)}
          warn={!!profile.last_auth_error_at}
        />
        <Def label={t('storeActivity.profile.scopes')} value={profile.scopes} wide />
      </DefGroup>
    </Panel>
  );
}

/**
 * The state's NAME, not its enum value, then what it does to the merchant,
 * what the writer recorded as the cause, and when the store entered it.
 * Each can be absent independently, and collapsing them would make an
 * absent reason look like a missing explanation.
 *
 * `blocked` / `payment_on_hold` are row FLAGS rather than two yes/no rows,
 * because they can be set under a state that does not name them: an open
 * `access_grace_until` reads `grace` over a blocked row, and "granted today,
 * locked out the day grace ends" is exactly what a support question is about.
 */
function BillingPanel({ billing }) {
  const { t } = useTranslation();
  const { formatCurrency, formatDate, formatDateTime } = useFormatters();
  const {
    subscription, revenue, state_since: stateSince, events,
    events_truncated: eventsTruncated,
  } = billing;
  const money = (value, currency) => (
    value === null || value === undefined ? null : formatCurrency(value, currency)
  );
  const day = (value) => (value ? formatDate(value) : null);
  const meaning = stateMeaning(billing.status, t);
  const cause = stateSince ? reasonLabel(stateSince.reason, t) : null;

  return (
    <Panel
      className="sa-panel--wide"
      icon={<Receipt size={ICON.md} />}
      title={t('storeActivity.sections.billing')}
      subtitle={t('storeActivity.sections.billingSubtitle')}
    >
      <DefGroup title={t('storeActivity.billing.groups.state')}>
        <Def label={t('storeActivity.billing.status')} value={stateLabel(billing.status, t)} />
        <Def
          label={t('storeActivity.billing.accessGranted')}
          value={t(billing.access_granted
            ? 'storeActivity.billing.access.granted'
            : 'storeActivity.billing.access.denied')}
          warn={!billing.access_granted}
        />
        <Def
          label={t('storeActivity.billing.stateSince')}
          value={stateSinceLabel(stateSince, t, formatDateTime)}
        />
        {meaning && <Def label={t('storeActivity.billing.meaning')} value={meaning} wide />}
        {cause && <Def label={t('storeActivity.billing.cause')} value={cause} wide />}
      </DefGroup>

      {subscription === null
        ? <div className="sa-panel-empty">{t('storeActivity.billing.noSubscription')}</div>
        : (
          <DefGroup title={t('storeActivity.billing.groups.plan')}>
            <Def label={t('storeActivity.billing.tier')} value={subscription.tier} />
            <Def label={t('storeActivity.billing.price')} value={money(subscription.price_usd, 'USD')} />
            <Def label={t('storeActivity.billing.interval')} value={subscription.interval} />
            <Def label={t('storeActivity.billing.paidUntil')} value={day(subscription.paid_until)} />
            {/* Nullable on the model: Shopify reported no trial mirror for
                this subscription. That is not "zero free days" — it is a
                platform answer we never got, so the row stays empty. */}
            <Def
              label={t('storeActivity.billing.trialDays')}
              value={subscription.trial_days === null
                ? null
                : t('storeActivity.profile.days', { count: subscription.trial_days })}
            />
            <Def
              label={t('storeActivity.billing.approvedAt')}
              value={subscription.platform_created_at
                ? formatDateTime(subscription.platform_created_at) : null}
            />
            <Def label={t('storeActivity.billing.locks')} value={lockBadges(subscription, t)} wide />
            <Def label={t('storeActivity.billing.graceUntil')} value={day(subscription.access_grace_until)} />
            <Def label={t('storeActivity.billing.shopifyGid')} value={subscription.shopify_gid} wide />
          </DefGroup>
        )}

      <DefGroup title={t('storeActivity.billing.groups.revenue')}>
        <Def label={t('storeActivity.billing.revenueUsd')} value={money(revenue.annual_usd, 'USD')} />
        <Def
          label={t('storeActivity.billing.revenueShop')}
          value={money(revenue.annual_shop_currency, revenue.shop_currency)}
        />
        {/* The rate and the day it was read, not only the converted figure: a
            tier the merchant disputes is argued from those two. */}
        <Def label={t('storeActivity.billing.fxRate')} value={revenue.fx_rate} />
        <Def label={t('storeActivity.billing.fxRateDate')} value={day(revenue.fx_rate_date)} />
        <Def
          label={t('storeActivity.billing.measuredAt')}
          value={revenue.calculated_at ? formatDateTime(revenue.calculated_at) : null}
        />
        <Def label={t('storeActivity.billing.firstOrder')} value={day(revenue.first_order_date)} />
      </DefGroup>

      <BillingTrail events={events} truncated={eventsTruncated} />
    </Panel>
  );
}

/**
 * The lock flags that are actually set, or `null`.
 *
 * Named flags rather than two Yes/No rows: "Blocked: No" costs a line and
 * says nothing, and reading down a column of No's is how the one Yes gets
 * missed. `null` rather than an empty element, so an unlocked row falls
 * through to the panel's own em dash.
 */
function lockBadges(subscription, t) {
  const set = [
    subscription.blocked && 'blocked',
    subscription.payment_on_hold && 'paymentOnHold',
  ].filter(Boolean);
  if (set.length === 0) return null;
  return (
    <span className="sa-locks">
      {set.map((key) => (
        <span className="sa-badge sa-badge--warn" key={key}>
          {t(`storeActivity.billing.lockNames.${key}`)}
        </span>
      ))}
    </span>
  );
}

/**
 * Every subscription transition this store has, newest first — the
 * difference between "this merchant is blocked" and "this merchant was
 * blocked at the reinstall on 12 August, having been on Growth since June".
 *
 * A store with no transitions renders nothing at all rather than an empty
 * state: every store that never approved a charge has none.
 */
function BillingTrail({ events, truncated }) {
  const { t } = useTranslation();
  const { formatDateTime } = useFormatters();
  if (events.length === 0) return null;
  return (
    <div className="sa-subsection">
      <div className="sa-subhead">{t('storeActivity.billing.trail')}</div>
      {events.map((event, i) => (
        <div className="sa-list-row" key={i}>
          <div className="sa-list-main">
            <div className="sa-list-primary">{transitionLabel(event, t)}</div>
            <div className="sa-list-secondary">
              {/* The reason is genuinely optional — a mirror sync names none —
                  so the separator has to go with it. */}
              {[
                formatDateTime(event.occurred_at),
                triggerLabel(event.trigger, t),
                reasonLabel(event.reason, t),
              ].filter(Boolean).join(' · ')}
            </div>
          </div>
        </div>
      ))}
      {truncated && (
        <div className="sa-panel-note">{t('storeActivity.truncated.billingEvents')}</div>
      )}
    </div>
  );
}

/**
 * Every address and account attached to this store.
 *
 * "The owner" means three different rows depending on who is asking, so all
 * three are shown and none stands in for another: the Shopify contact address
 * captured at install, the organization's billing contact, and the accounts
 * with access. Names come from Shopify at sign-in, so an account can
 * legitimately have an email and no name.
 *
 * An access row says who and how — `granted_via` in prose, dated — and the
 * language their Shopify admin ran in at their last Shopify sign-in, when
 * there was one. It does NOT
 * report when the person was last active: every per-user timestamp we hold is
 * written by SaaS sign-in alone, so for the Shopify staff who work inside the
 * admin it would read "never" no matter how much they use the app.
 */
function PeoplePanel({ people }) {
  const { t } = useTranslation();
  const { formatDate } = useFormatters();
  const { organization: org, access } = people;
  // `granted_at` is auto_now_add (core.models.StoreAccess) — always a date.
  // The email leads only when a name took the primary line.
  const secondaryOf = (person) => [
    ...(person.name ? [person.email] : []),
    t(`storeActivity.people.grantedVia.${person.granted_via}`),
    formatDate(person.granted_at),
    ...(person.shopify_locale
      ? [`${t('storeActivity.people.adminLanguage')} ${person.shopify_locale}`]
      : []),
  ].join(' · ');

  return (
    <Panel icon={<UserCircle size={ICON.md} />} title={t('storeActivity.sections.people')} count={access.length}>
      <DefGroup title={t('storeActivity.people.groups.contacts')}>
        <Def label={t('storeActivity.people.contactEmail')} value={people.contact_email} wide />
        <Def label={t('storeActivity.people.companyEmail')} value={people.company_email} />
        <Def label={t('storeActivity.people.companyPhone')} value={people.company_phone} />
        {org === null
          ? (
            <Def
              label={t('storeActivity.people.organization')}
              value={t('storeActivity.people.noOrganization')}
              wide
            />
          )
          : (
            <>
              <Def label={t('storeActivity.people.organization')} value={org.name} />
              <Def label={t('storeActivity.people.notifications')} value={org.notifications_to} />
            </>
          )}
      </DefGroup>

      <DefGroup title={t('storeActivity.people.groups.accounts')}>
        {access.length === 0
          ? <div className="sa-panel-empty">{t('storeActivity.people.noAccess')}</div>
          : (
            <div className="sa-list">
              {access.map((person) => (
                <div
                  className={`sa-list-row${person.revoked_at ? ' sa-person--revoked' : ''}`}
                  key={person.email}
                >
                  <span className="sa-chip sa-chip--system">{person.access_type}</span>
                  <div className="sa-list-main">
                    <div className="sa-list-primary">
                      {person.name ? person.name : person.email}
                      {person.account_owner && (
                        <span className="sa-badge">{t('storeActivity.people.accountOwner')}</span>
                      )}
                      {person.collaborator && (
                        <span className="sa-badge">{t('storeActivity.people.collaborator')}</span>
                      )}
                      {person.revoked_at && (
                        <span className="sa-badge sa-badge--warn">{t('storeActivity.people.revoked')}</span>
                      )}
                    </div>
                    <div className="sa-list-secondary">
                      {secondaryOf(person)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
      </DefGroup>
    </Panel>
  );
}

function SearchesPanel({ searches, truncated }) {
  const { t } = useTranslation();
  const { formatNumber } = useFormatters();
  return (
    <Panel icon={<MagnifyingGlass size={ICON.md} />} title={t('storeActivity.sections.searches')} count={searches.length}>
      {searches.length === 0
        ? <div className="sa-panel-empty">{t('storeActivity.empty.searches')}</div>
        : (
          <div className="sa-list">
            {searches.map((s, i) => (
              <div className="sa-list-row" key={i}>
                <div className="sa-list-main">
                  <div className="sa-list-primary">{s.query}</div>
                  <div className="sa-list-secondary">
                    {s.selected_path || t('storeActivity.columns.noClick')}
                  </div>
                </div>
                <span className="sa-list-value">{formatNumber(s.result_count, 0)}</span>
              </div>
            ))}
          </div>
        )}
      {truncated && (
        <div className="sa-panel-note">{t('storeActivity.truncated.searches')}</div>
      )}
    </Panel>
  );
}
